import BreadCrumbs from '@components/Breadcrumbs';
import Button from '@components/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import { apiClient } from '@services/api';
import { capitalizeFirstLetter, dateLocale, translator } from '@utils/helpers';
import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import Moment from 'react-moment';
import NumberFormat from 'react-number-format';
import { useParams } from 'react-router-dom';
import ReactToPrint from 'react-to-print';

const palette = {
  pageBg: '#0f1e35',
  card: '#172B4D',
  accent: '#62DEC2',
  textSec: '#8ba5c4',
  textMain: '#e8f0fb',
  hover: '#1e3a5a',
  sep: '#1a3050',
};

const printStyles = `
  @media print {
    .ft-doc-root {
      background: #ffffff !important;
      color: #111111 !important;
    }
    .ft-doc-header {
      background: #ffffff !important;
      border-bottom: 2px solid #cccccc !important;
    }
    .ft-doc-section-title {
      color: #0f1e35 !important;
      border-bottom: 1px solid #cccccc !important;
    }
    .ft-doc-card {
      background: #f5f7fa !important;
      border: 1px solid #dde3ec !important;
    }
    .ft-doc-label {
      color: #555555 !important;
    }
    .ft-doc-value {
      color: #111111 !important;
    }
    .ft-doc-total-box {
      background: #0f1e35 !important;
      color: #ffffff !important;
    }
    .ft-doc-sep {
      border-color: #cccccc !important;
    }
    .ft-doc-tag {
      background: #e0f7f3 !important;
      color: #0f5149 !important;
    }
    .ft-doc-footer {
      border-top: 1px solid #cccccc !important;
      color: #555555 !important;
    }
    .ft-doc-logo {
      mix-blend-mode: normal !important;
      background: transparent !important;
    }
  }
`;

class Content extends React.Component {
  render() {
    const props = this.props;
    const idiom = (props.voucher.customerData.language || 'pt').toUpperCase();

    const calcBruto = () => {
      return props.voucher.budgets.reduce((acc, v) => {
        const subtotal = v.travel_type === 'tour'
          ? parseFloat(v?.adults_total_amount) + parseFloat(v?.kids_total_amount)
          : parseInt(v?.adults_total) * parseFloat(v?.total_amount || 0);
        const discount = v?.discount_total_sale
          ? parseFloat(v?.discount_total_amount) + parseFloat((parseFloat(v?.price) - parseFloat(v?.discount_total_amount)) * parseFloat(v?.discount_total_sale) / 100)
          : parseFloat(v?.discount_total_amount || 0);
        return acc + subtotal - discount;
      }, 0);
    };

    const calcManagerDiscount = () => {
      const ps = props.voucher;
      if (ps?.approval_status !== 'approved') return 0;
      if (!['discount_pct', 'discount_value'].includes(ps?.approval_type)) return 0;
      if (!ps?.approval_manager_value) return 0;
      const bruto = calcBruto();
      if (ps.approval_type === 'discount_pct') {
        return Math.round(bruto * parseFloat(ps.approval_manager_value) / 100 * 100) / 100;
      }
      return Math.round(parseFloat(ps.approval_manager_value) * 100) / 100;
    };

    const calcGrandTotal = () => {
      return Math.max(0, calcBruto() - calcManagerDiscount());
    };

    const s = {
      root: {
        background: palette.pageBg,
        color: palette.textMain,
        borderRadius: '12px',
        padding: '36px 40px',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        fontSize: '14px',
        lineHeight: '1.6',
      },
      header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: '28px',
        borderBottom: `2px solid ${palette.sep}`,
        marginBottom: '32px',
      },
      logoWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
      },
      logoImg: {
        width: '72px',
        height: '72px',
        objectFit: 'cover',
        borderRadius: '50%',
        border: `2px solid ${palette.accent}`,
        mixBlendMode: 'screen',
      },
      companyName: {
        color: palette.accent,
        fontWeight: '700',
        fontSize: '13px',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      },
      companyAddress: {
        textAlign: 'right',
        color: palette.textSec,
        fontSize: '12px',
        lineHeight: '1.8',
      },
      docTitle: {
        textAlign: 'center',
        marginBottom: '28px',
      },
      docTitleText: {
        fontSize: '22px',
        fontWeight: '700',
        color: palette.textMain,
        letterSpacing: '0.02em',
      },
      refBadge: {
        display: 'inline-block',
        marginTop: '8px',
        background: palette.hover,
        border: `1px solid ${palette.accent}`,
        color: palette.accent,
        borderRadius: '20px',
        padding: '4px 16px',
        fontSize: '12px',
        fontWeight: '600',
        letterSpacing: '0.06em',
      },
      sectionTitle: {
        fontSize: '13px',
        fontWeight: '700',
        color: palette.accent,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        paddingBottom: '10px',
        borderBottom: `1px solid ${palette.sep}`,
        marginBottom: '16px',
        marginTop: '28px',
      },
      summaryGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 2fr 1fr',
        gap: '0',
        marginBottom: '8px',
      },
      summaryHeader: {
        padding: '10px 14px',
        background: palette.hover,
        color: palette.textSec,
        fontSize: '11px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        borderBottom: `1px solid ${palette.sep}`,
      },
      summaryCell: {
        padding: '10px 14px',
        color: palette.textMain,
        fontSize: '13px',
        borderBottom: `1px solid ${palette.sep}`,
      },
      budgetCard: {
        background: palette.card,
        border: `1px solid ${palette.sep}`,
        borderRadius: '10px',
        padding: '20px 24px',
        marginBottom: '16px',
      },
      budgetCardTitle: {
        fontSize: '16px',
        fontWeight: '700',
        color: palette.textMain,
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      },
      tag: {
        background: `${palette.accent}20`,
        color: palette.accent,
        border: `1px solid ${palette.accent}40`,
        borderRadius: '6px',
        padding: '2px 10px',
        fontSize: '11px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      },
      budgetRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '12px',
        marginBottom: '12px',
      },
      budgetField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
      },
      fieldLabel: {
        fontSize: '11px',
        fontWeight: '600',
        color: palette.textSec,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
      fieldValue: {
        fontSize: '14px',
        color: palette.textMain,
        fontWeight: '500',
      },
      amountValue: {
        fontSize: '15px',
        color: palette.textMain,
        fontWeight: '600',
      },
      discountValue: {
        fontSize: '15px',
        color: '#f87171',
        fontWeight: '600',
      },
      totalValue: {
        fontSize: '16px',
        color: palette.accent,
        fontWeight: '700',
      },
      budgetDivider: {
        border: 'none',
        borderTop: `1px solid ${palette.sep}`,
        margin: '12px 0',
      },
      infoGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px 32px',
      },
      infoField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        paddingBottom: '8px',
        borderBottom: `1px solid ${palette.sep}`,
      },
      grandTotalBox: {
        background: palette.card,
        border: `2px solid ${palette.accent}`,
        borderRadius: '10px',
        padding: '20px 28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '28px',
      },
      grandTotalLabel: {
        fontSize: '16px',
        fontWeight: '700',
        color: palette.textMain,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      },
      grandTotalAmount: {
        fontSize: '26px',
        fontWeight: '800',
        color: palette.accent,
      },
      footer: {
        marginTop: '36px',
        paddingTop: '20px',
        borderTop: `1px solid ${palette.sep}`,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '8px',
        color: palette.textSec,
        fontSize: '12px',
        textAlign: 'center',
      },
    };

    const currency = process.env.REACT_APP_CURRENCY;

    const formatNum = (value) => (
      <NumberFormat
        value={value}
        displayType={'text'}
        decimalSeparator={','}
        decimalScale={2}
        fixedDecimalScale={true}
        thousandSeparator={'.'}
        isNumericString
        prefix={`${currency} `}
        renderText={(v) => <span>{v}</span>}
      />
    );

    return (
      <>
        <style>{printStyles}</style>
        <div className="ft-doc-root" style={s.root}>

          {/* ── Header ── */}
          <div className="ft-doc-header" style={s.header}>
            <div className="ft-doc-logo" style={s.logoWrap}>
              {props.voucher.company.logo && (
                <img
                  src={`${process.env.REACT_APP_BASE_IMAGE_URL}/${props.voucher.company.logo}`}
                  alt={process.env.REACT_APP_NAME}
                  style={s.logoImg}
                />
              )}
              <span style={s.companyName}>{process.env.REACT_APP_NAME}</span>
            </div>
            <div style={s.docTitle}>
              <div style={s.docTitleText}>Documento de Orçamento</div>
              {props.voucher.references && (
                <div className="ft-doc-tag" style={s.refBadge}>
                  Ref: {props.voucher.references}
                </div>
              )}
            </div>
            <div className="ft-doc-label" style={s.companyAddress}>
              <div>
                {props.voucher.company.address}{props.voucher.company.number ? `, ${props.voucher.company.number}` : ''}
                {props.voucher.company.complement ? ` - ${props.voucher.company.complement}` : ''}
              </div>
              {props.voucher.company.district && props.voucher.company.district !== 'null' && (
                <div>{props.voucher.company.district}</div>
              )}
              <div>{props.voucher.company.state} — {props.voucher.company.country}</div>
              <div>{translator(idiom, 'voucher', 'rut')}: {props.voucher.company.rut}</div>
              <div>{translator(idiom, 'voucher', 'phone')}: {props.voucher.company.phone}</div>
            </div>
          </div>

          {/* ── Summary table ── */}
          <div className="ft-doc-section-title" style={s.sectionTitle}>Resumo dos Itens</div>
          <div style={{ border: `1px solid ${palette.sep}`, borderRadius: '8px', overflow: 'hidden', marginBottom: '8px' }}>
            <div style={s.summaryGrid}>
              <div className="ft-doc-label" style={{ ...s.summaryHeader, borderRadius: '0' }}>Data</div>
              <div className="ft-doc-label" style={{ ...s.summaryHeader }}>Produto / Destino</div>
              <div className="ft-doc-label" style={{ ...s.summaryHeader, textAlign: 'right' }}>Pax</div>
            </div>
            {props.voucher.budgets.map((v, i) => (
              <div key={i} style={s.summaryGrid}>
                <div style={s.summaryCell}>
                  <Moment format="DD/MM/YYYY" date={v?.tour_date} />
                </div>
                <div style={s.summaryCell}>
                  {v.travel_type !== 'transfer' && v?.site_slug ? (
                    <a
                      href={`https://fulltour.com.br/tour/${v.site_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: palette.textMain, textDecoration: 'none' }}
                    >
                      {v?.travel?.map((t) => t.region_name || t.name).join(', ')}
                    </a>
                  ) : (
                    <>
                      {v?.travel?.map((t) => t.region_name || t.name).join(', ')}
                      {v?.transfer_type ? ` — Transfer ${v.transfer_type}` : ''}
                    </>
                  )}
                </div>
                <div style={{ ...s.summaryCell, textAlign: 'right' }}>
                  {v?.travel_type === 'tour'
                    ? parseInt(v?.adults_total) + parseInt(v?.kids_total || 0)
                    : v?.cupos}
                </div>
              </div>
            ))}
          </div>

          {/* ── Budget detail ── */}
          <div className="ft-doc-section-title" style={s.sectionTitle}>Detalhamento do Orçamento</div>
          {props.voucher.budgets.map((v, i) => {
            const subtotal = v.travel_type === 'tour'
              ? parseFloat(v?.adults_total_amount) + parseFloat(v?.kids_total_amount)
              : parseInt(v?.adults_total) * parseFloat(v?.total_amount || 0);
            const discount = v?.discount_total_sale
              ? parseFloat(v?.discount_total_amount) + parseFloat((parseFloat(v?.price) - parseFloat(v?.discount_total_amount)) * parseFloat(v?.discount_total_sale) / 100)
              : parseFloat(v?.discount_total_amount || 0);
            const total = subtotal - discount;
            const adultPrice = v.travel_type === 'tour' ? v?.travel[0]?.price : v?.price;
            const kidPrice = v?.travel[0]?.kids_price;

            return (
              <div key={i} className="ft-doc-card" style={s.budgetCard}>
                <div style={s.budgetCardTitle}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {v.travel_type !== 'transfer' && v?.site_slug ? (
                      <>
                        <a
                          href={`https://fulltour.com.br/tour/${v.site_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: palette.textMain, textDecoration: 'none', fontWeight: '700', fontSize: '16px' }}
                        >
                          {v?.travel?.map((t) => t.region_name || t.name).join(', ')}
                        </a>
                        <a
                          href={`https://fulltour.com.br/tour/${v.site_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '11px', color: palette.accent, textDecoration: 'none', fontWeight: '500', letterSpacing: '0.03em', opacity: 0.85 }}
                        >
                          Clique e saiba mais ↗
                        </a>
                      </>
                    ) : (
                      <span>
                        {v?.travel?.map((t) => t.region_name || t.name).join(', ')}
                        {v?.transfer_type ? ` — Transfer ${v.transfer_type}` : ''}
                      </span>
                    )}
                  </div>
                  <span className="ft-doc-tag" style={s.tag}>{v?.travel_type || 'tour'}</span>
                </div>

                <div style={s.budgetRow}>
                  <div style={s.budgetField}>
                    <span className="ft-doc-label" style={s.fieldLabel}>Data do passeio</span>
                    <span className="ft-doc-value" style={s.fieldValue}>
                      <Moment format="DD/MM/YYYY" date={v?.tour_date} />
                    </span>
                  </div>
                  <div style={s.budgetField}>
                    <span className="ft-doc-label" style={s.fieldLabel}>Adultos</span>
                    <span className="ft-doc-value" style={s.fieldValue}>{parseInt(v?.adults_total)}</span>
                  </div>
                  <div style={s.budgetField}>
                    <span className="ft-doc-label" style={s.fieldLabel}>Crianças</span>
                    <span className="ft-doc-value" style={s.fieldValue}>{v?.kids_total ? parseInt(v?.kids_total) : 0}</span>
                  </div>
                </div>

                <div style={s.budgetRow}>
                  <div style={s.budgetField}>
                    <span className="ft-doc-label" style={s.fieldLabel}>{translator(idiom, 'voucher', 'value_by_adult')}</span>
                    <span style={s.amountValue}>{formatNum(adultPrice)}</span>
                  </div>
                  {kidPrice && (
                    <div style={s.budgetField}>
                      <span className="ft-doc-label" style={s.fieldLabel}>Valor por criança</span>
                      <span style={s.amountValue}>{formatNum(kidPrice)}</span>
                    </div>
                  )}
                  <div style={s.budgetField}>
                    <span className="ft-doc-label" style={s.fieldLabel}>Validade do orçamento</span>
                    <span className="ft-doc-value" style={s.fieldValue}>
                      <Moment format="DD/MM/YYYY" date={format(new Date(`${v?.created_at}`), "yyyy-MM-dd")} />
                    </span>
                  </div>
                </div>

                <hr style={s.budgetDivider} />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', alignItems: 'flex-end' }}>
                  <div style={s.budgetField}>
                    <span className="ft-doc-label" style={s.fieldLabel}>Subtotal</span>
                    <span style={s.amountValue}>{formatNum(subtotal)}</span>
                  </div>
                  <div style={s.budgetField}>
                    <span className="ft-doc-label" style={s.fieldLabel}>Desconto</span>
                    <span style={s.discountValue}>- {formatNum(discount)}</span>
                  </div>
                  <div style={s.budgetField}>
                    <span className="ft-doc-label" style={s.fieldLabel}>Total do item</span>
                    <span style={s.totalValue}>{formatNum(total)}</span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Desconto gerencial ── */}
          {calcManagerDiscount() > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'rgba(245,200,66,0.08)', border: '1px solid rgba(245,200,66,0.3)',
              borderRadius: '10px', padding: '14px 28px', marginTop: '16px',
            }}>
              <span style={{ color: '#F5C842', fontWeight: '700', fontSize: '14px' }}>
                ★ Desconto gerencial
                {props.voucher?.approval_type === 'discount_pct'
                  ? ` (${props.voucher.approval_manager_value}%)`
                  : ''}
              </span>
              <span style={{ color: '#F5C842', fontWeight: '800', fontSize: '18px' }}>
                − {formatNum(calcManagerDiscount())}
              </span>
            </div>
          )}

          {/* ── Grand total ── */}
          <div className="ft-doc-total-box" style={s.grandTotalBox}>
            <span style={s.grandTotalLabel}>Total Geral</span>
            <span style={s.grandTotalAmount}>{formatNum(calcGrandTotal())}</span>
          </div>

          {/* ── Customer ── */}
          <div className="ft-doc-section-title" style={s.sectionTitle}>{translator(idiom, 'voucher', 'customers_data')}</div>
          <div style={s.infoGrid}>
            <div style={s.infoField}>
              <span className="ft-doc-label" style={s.fieldLabel}>{translator(idiom, 'voucher', 'customer_name')}</span>
              <span className="ft-doc-value" style={s.fieldValue}>{props.voucher.customer.name}</span>
            </div>
            <div style={s.infoField}>
              <span className="ft-doc-label" style={s.fieldLabel}>{translator(idiom, 'voucher', 'phone')}</span>
              <span className="ft-doc-value" style={s.fieldValue}>{props.voucher.customer.phone}</span>
            </div>
            <div style={s.infoField}>
              <span className="ft-doc-label" style={s.fieldLabel}>{translator(idiom, 'voucher', 'email')}</span>
              <span className="ft-doc-value" style={s.fieldValue}>{props.voucher.customer.email}</span>
            </div>
            <div style={s.infoField}>
              <span className="ft-doc-label" style={s.fieldLabel}>{translator(idiom, 'voucher', 'document')}</span>
              <span className="ft-doc-value" style={s.fieldValue}>{props.voucher.customer.document}</span>
            </div>
            {props.voucher?.reservationData?.combo_id && (
              <div style={{ ...s.infoField, gridColumn: '1 / -1' }}>
                <span className="ft-doc-label" style={s.fieldLabel}>{translator(idiom, 'voucher', 'address')}</span>
                <span className="ft-doc-value" style={s.fieldValue}>{props.voucher.reservationData.address_hotel}</span>
              </div>
            )}
          </div>

          {/* ── Seller ── */}
          <div className="ft-doc-section-title" style={s.sectionTitle}>Informações do Vendedor</div>
          <div style={s.infoGrid}>
            <div style={s.infoField}>
              <span className="ft-doc-label" style={s.fieldLabel}>Nome do vendedor</span>
              <span className="ft-doc-value" style={s.fieldValue}>{props.voucher?.seller[0]?.name}</span>
            </div>
            <div style={s.infoField}>
              <span className="ft-doc-label" style={s.fieldLabel}>E-mail</span>
              <span className="ft-doc-value" style={s.fieldValue}>{props.voucher?.seller[0]?.email}</span>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="ft-doc-footer" style={s.footer}>
            <div>{translator(idiom, 'voucher', 'customer_name')}: {props.voucher.reservationData.customer_name}</div>
            <div>ID: {props.voucher.id} &nbsp;|&nbsp; Data: {dateLocale(props.voucher.created_at)}</div>
            <div>Esse orçamento pode sofrer alterações</div>
          </div>

        </div>
      </>
    );
  }
}

const Show = (props) => {
  document.title = `Documento do orçamento - ${process.env.REACT_APP_NAME}`;
  const componentRef = useRef();
  const { travel_type, travel_id, presale_id } = useParams();
  const [voucher, setVoucher] = useState({});
  const [loading, setLoading] = useState(true);
  const [sendingVoucher, setSendingVoucher] = useState(false);
  let url = `presales/${presale_id}/document`;
  const [success, setSuccess] = useState(false);
  const sendDocumentUrl = `presales/${presale_id}/send_document`;

  const bradecrumbsConfig = [
    {
      title: 'Início',
      active: true,
      route: '/admin/orcamentos',
    },
    {
      title: 'Novo orçamento',
      active: false,
      route: null,
    },
    {
      title: `${capitalizeFirstLetter(travel_type)}`,
      active: true,
      route: `/admin/orcamentos/${travel_type}`,
    },
    {
      title: 'Comprar',
      active: true,
      route: `/admin/orcamentos/${travel_type}/${travel_id}/comprar`,
    },
    {
      title: 'Orçamento',
      active: true,
      route: `/admin/orcamentos/detalhe/${presale_id}`,
    },
    {
      title: 'Documento',
      active: false,
    },
  ];

  const getVoucher = (url) => {
    apiClient.httpInstance.get(url).then((response) => {
      if (response.status === 200) {
        const d = response.data;
        const sortByDate = arr => (arr || []).slice().sort((a, b) => {
          const ta = a.tour_date ? new Date(a.tour_date + 'T00:00:00').getTime() : Infinity;
          const tb = b.tour_date ? new Date(b.tour_date + 'T00:00:00').getTime() : Infinity;
          return ta - tb;
        });
        if (d.reservations) d.reservations = sortByDate(d.reservations);
        if (d.budgets)      d.budgets      = sortByDate(d.budgets);
        setVoucher(d);
        setLoading(false);
      }
    });
  };

  const sendDocument = () => {
    setSendingVoucher(true);
    apiClient.httpInstance.post(sendDocumentUrl).then((response) => {
      if (response.status === 200) {
        setSuccess(true);
        setSendingVoucher(false);
        setTimeout(() => {
          setSuccess(false);
        }, 2000);
      }
    });
  };

  useEffect(() => {
    getVoucher(url);
  }, [url]);

  return (
    <>
      {!loading && (
        <>
          <BreadCrumbs items={bradecrumbsConfig} />
          <div className="px-7 pt-6">
            <div className="flex items-center justify-between mt-2 text-2xl text-blue">
              <span>Orçamento</span>
              {!voucher?.discount_total_sale && (
                <div className="space-x-3 w-100">
                  <Button
                    textcolor="#e8f0fb"
                    padding="6px 15px"
                    width="200px"
                    variant="outlined"
                    disabled={sendingVoucher}
                    onClick={sendDocument}
                    bgColor="transparent"
                  >
                    Enviar por e-mail{' '}
                    <span className="ml-2">
                      <CircularProgress
                        style={{ display: sendingVoucher ? 'block' : 'none' }}
                        size={15}
                      />
                    </span>
                  </Button>
                  <ReactToPrint
                    trigger={() => (
                      <Button
                        textcolor="#FFF"
                        padding="6px 15px"
                        width="200"
                        customcolor={props.theme.color}
                      >
                        Visualizar PDF
                      </Button>
                    )}
                    content={() => componentRef.current}
                  />
                </div>
              )}
            </div>
            {success && (
              <Alert className="mt-5" severity="success" color="success">
                Documento enviado com sucesso
              </Alert>
            )}
            <Content {...props} voucher={voucher} ref={componentRef} />
          </div>
        </>
      )}
    </>
  );
};

export default Show;
